import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";

export interface SchoolLookupResult {
  schoolCode: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  schoolType: string;
}

export const MIN_SCHOOL_SEARCH_LENGTH = 3;

interface UseFindSchoolsParams {
  search?: string;
  state?: string;
  schoolType?: string;
}

export const useFindSchools = ({ search, state, schoolType }: UseFindSchoolsParams) => {
  const { getToken } = useAccessToken();
  const normalizedSearch = search?.trim() ?? "";
  const normalizedState = state?.trim() ?? "";
  const normalizedSchoolType = schoolType?.trim() ?? "";

  const query = useQuery({
    queryKey: ["findSchools", normalizedSearch, normalizedState, normalizedSchoolType],
    queryFn: async () =>
      findSchools(await getToken(), {
        search: normalizedSearch,
        state: normalizedState,
        schoolType: normalizedSchoolType
      }),
    enabled:
      normalizedSearch.length >= MIN_SCHOOL_SEARCH_LENGTH &&
      normalizedState.length > 0 &&
      normalizedSchoolType.length > 0
  });

  const schools = useMemo(() => query.data?.schools ?? [], [query.data]);

  return {
    schools,
    isLoading: query.isLoading
  };
};

const findSchools = (
  accessToken: string,
  {
    search,
    state,
    schoolType
  }: {
    search: string;
    state: string;
    schoolType: string;
  }
) => {
  const params = new URLSearchParams({
    search,
    state,
    school_type: schoolType
  });

  return callLcosService<{ schools: SchoolLookupResult[] }>(accessToken, `/api/schools?${params.toString()}`, "GET");
};
